import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import { Box, MenuItem, Select, Stack, TextField } from "@mui/material";
import type { UnitType } from "adl-gen/ferovinum/app/db";
import { ErrorMessage, type FormikProps } from "formik";
import type React from "react";
import { isCase, isSinglesUnitType } from "utils/model-utils";
import { FormikError } from "../../../utils/formik";

export const SINGLES_PER_CASE_OPTIONS = [
  { label: "Select", value: "" },
  { label: "1 singles / case", value: 1 },
  { label: "3 singles / case", value: 3 },
  { label: "6 singles / case", value: 6 },
  { label: "12 singles / case", value: 12 },
  { label: "24 singles / case", value: 24 },
  { label: "Other", value: "other" },
] as const;

export interface SinglesPerCaseInfo {
  singlesPerCase: number | undefined;
}

interface SinglesPerCaseDropdownProps {
  index: number;
  form: FormikProps<any>;
  productQty: number;
  isOtherSelected: boolean;
  onOtherSelectionChange: (isSelected: boolean) => void;
}

export const SinglesPerCaseDropdown: React.FC<SinglesPerCaseDropdownProps> = ({
  index,
  form,
  productQty,
  isOtherSelected,
  onOtherSelectionChange,
}) => {
  const err = FormikError(form);
  return (
    <Stack spacing={2} alignItems="center">
      <Select
        displayEmpty
        renderValue={(value: number | string) => {
          if (value === "" || value === null) {
            return (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, pr: 4 }}
              >
                <span>Select</span>
                <ErrorOutlineIcon color="warning" fontSize="small" />
              </Box>
            );
          }
          if (value === "other") return "Other";
          return `${value} singles / case`;
        }}
        value={
          isOtherSelected
            ? "other"
            : (form.values.singlesPerCases[index]?.singlesPerCase ?? "")
        }
        sx={{ maxWidth: "75%" }}
        onChange={(e) => {
          const isOther = e.target.value === "other";
          onOtherSelectionChange(isOther);

          const value = isOther
            ? undefined
            : e.target.value === ""
              ? ""
              : Number(e.target.value);
          form.setFieldValue(`singlesPerCases[${index}].singlesPerCase`, value);
        }}
      >
        {SINGLES_PER_CASE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {isOtherSelected && (
        <TextField
          name={`singlesPerCases[${index}].singlesPerCase`}
          label="Custom singles per case"
          type="number"
          inputProps={{ min: 1, max: productQty }}
          value={form.values.singlesPerCases[index]?.singlesPerCase ?? ""}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          error={err(`singlesPerCases[${index}].singlesPerCase`)}
          helperText={
            <ErrorMessage name={`singlesPerCases[${index}].singlesPerCase`} />
          }
        />
      )}
    </Stack>
  );
};

export const ShouldDisplaySinglesPerCaseDropdown = (unitType: UnitType) => {
  return isSinglesUnitType(unitType) || isCase(unitType);
};
